/**
 * Labstep
 *
 * @module grid/SmartTableProtocolValue/coldefs/metadata/file
 * @desc Column Definition for metadata file
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import FileActionCreate from 'labstep-web/components/File/Action/Create';
import { FileActionRemove } from 'labstep-web/components/File/Action/Remove';
import { FilePreviewContainer as FilePreview } from 'labstep-web/components/File/Preview';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import type {
  CellRendererParams,
  HeaderComponentParamsProps,
} from 'labstep-web/core/DataGrid/types';
import { stopEditingAndFocus } from 'labstep-web/core/DataGrid/utils';
import { ModalDefaultContainer as Modal } from 'labstep-web/core/Modal/Default';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import type { SmartTableColDef } from 'labstep-web/grid/SmartTableProtocolValue/coldefs/types';
import { GridSmartTableColumnHeader } from 'labstep-web/grid/SmartTableProtocolValue/components/ColumnHeader';
import { GridSmartTableProtocolValueService } from 'labstep-web/grid/SmartTableProtocolValue/services/grid-smart-table-protocol-value.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import type { Entity } from 'labstep-web/models/entity.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import type { File } from 'labstep-web/models/file.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import { Resource } from 'labstep-web/models/resource.model';
import { generateNewDateString } from 'labstep-web/services/date.service';
import { Label } from 'semantic-ui-react';

export const getMetadataFileColDef = (
  experimentOrProtocol: Experiment | Protocol,
  parentEntityClass: string,
  metadataType: MetadataType,
  metadataLabel: string,
  protocolValueName?: string,
): SmartTableColDef => {
  return {
    colId: protocolValueName
      ? `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}:${protocolValueName}`
      : `metadata:${parentEntityClass}:${metadataType}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridSmartTableColumnHeader,
    headerComponentParams: (params: HeaderComponentParamsProps) => {
      return {
        icon: protocolValueName
          ? ICONS.metadata.secondary
          : ICONS.metadata.type.file,
        cornerIcon:
          protocolValueName &&
          (parentEntityClass === Resource.entityName
            ? ICONS.resource.primary
            : ICONS.resource_item.primary),
        secondaryHeader:
          protocolValueName &&
          `${protocolValueName}${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}`,
        toolPanelColumnName: `${GridSmartTableProtocolValueService.getMetadataParentPrefix(parentEntityClass)}${metadataLabel}`,
        experimentOrProtocol,
      };
    },
    editable: (params: EditableCallbackParams) =>
      GridSmartTableProtocolValueService.getMetadataEditable(
        params,
        experimentOrProtocol,
        metadataType,
        metadataLabel,
        parentEntityClass,
        protocolValueName,
      ),
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      return metadata?.value;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }

      return false;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        return (
          <TextPlaceholder
            onClick={() => {
              GridSmartTableProtocolValueService.createMetadata(
                params,
                experimentOrProtocol,
                parentEntityClass,
                metadataType,
                metadataLabel,
                protocolValueName,
                {},
                () => {
                  GridSmartTableProtocolValueService.startEditingCell(
                    params,
                  );
                },
              );
            }}
          >
            Upload files
          </TextPlaceholder>
        );
      }

      const files = metadata ? metadata.filesGroupedByName : [];
      if (files.length > 1) {
        return (
          <Modal
            size="small"
            header="Files"
            viewComponent={({ toggleModal }) => (
              <TextPlaceholder onClick={toggleModal}>
                Multiple files
              </TextPlaceholder>
            )}
            content={({ toggleModal }) => (
              <div>
                {files.map((file: File) => (
                  <Label key={file.guid}>
                    <FilePreview
                      file={file}
                      text={file.name}
                      noDiv
                      withDefault
                    />
                    <FileActionRemove
                      file={file}
                      actionComponentProps={{
                        type: 'icon',
                        icon: 'close',
                        elementProps: { noDiv: true },
                      }}
                      options={GridSmartTableProtocolValueService.getOptions(
                        params,
                        () => {
                          if (!metadata.is_variable) {
                            GridSmartTableProtocolValueService.refreshColumn(
                              params,
                            );
                          }
                          toggleModal();
                        },
                      )}
                    />
                  </Label>
                ))}
              </div>
            )}
          />
        );
      } else if (files.length === 1) {
        const file = files[0];
        return (
          <Label key={file.guid}>
            <FilePreview
              file={file}
              text={file.name}
              noDiv
              withDefault
            />
            <FileActionRemove
              file={file}
              actionComponentProps={{
                type: 'icon',
                icon: 'close',
                elementProps: { noDiv: true },
              }}
              options={GridSmartTableProtocolValueService.getOptions(
                params,
                () => {
                  if (!metadata.is_variable) {
                    GridSmartTableProtocolValueService.refreshColumn(
                      params,
                    );
                  }
                },
              )}
            />
          </Label>
        );
      }

      return (
        <TextPlaceholder
          onClick={() => {
            GridSmartTableProtocolValueService.startEditingCell(
              params,
            );
          }}
        >
          Upload files
        </TextPlaceholder>
      );
    },
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (!metadata) {
        return null;
      }

      return (
        <FileActionCreate
          entityType={metadata.entityName}
          entityId={metadata.guid as any}
          onFolderWatcherSelect={({ file }): void => {
            GridSmartTableProtocolValueService.updateEntity(
              params,
              metadata.entityName,
              metadata.guid,
              {
                file_id: file.guid,
              },
            );
          }}
          options={{
            ...GridSmartTableProtocolValueService.getOptions(
              params,
              () => {
                if (!metadata.is_variable) {
                  GridSmartTableProtocolValueService.refreshColumn(
                    params,
                  );
                }
              },
            ),
          }}
          modalProps={{
            onClose: (): void => stopEditingAndFocus(params),
            open: true,
            viewComponent: null,
          }}
        />
      );
    },
    cellStyle: (params) =>
      GridSmartTableProtocolValueService.getCellStyle(
        params,
        experimentOrProtocol,
        Metadata.entityName,
        metadataLabel,
        parentEntityClass,
      ),
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: (params: any) => {
      if (!params.node) {
        return null;
      }

      const metadata =
        GridSmartTableProtocolValueService.getProtocolValueMetadata(
          experimentOrProtocol,
          params.node.data,
          parentEntityClass,
          metadataLabel,
          metadataType,
          protocolValueName,
        );

      if (metadata) {
        GridSmartTableProtocolValueService.updateEntity(
          params,
          metadata.entityName,
          metadata.guid,
          {
            deleted_at: generateNewDateString(),
          },
        );
      }
      return null;
    },
  };
};
